import React from 'react';
import { Save, Trash2, Play } from 'lucide-react';
import { useTimer } from '../../context/TimerContext';
import type { TimerPreset } from '../../lib/presets';
import { createPreset, deletePreset, listPresets } from '../../lib/presets';

function formatSummary(preset: TimerPreset): string {
  const fmt = (s: number) => {
    const m = Math.round(s / 60);
    return `${m}m`;
  };
  return `${fmt(preset.focus_duration)} / ${fmt(preset.short_break_duration)} / ${fmt(preset.long_break_duration)} × ${preset.cycle_count}`;
}

export function PresetsSection() {
  const { settings, updateSettings } = useTimer();
  const [presets, setPresets] = React.useState<TimerPreset[] | null>(null);
  const [name, setName] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    listPresets()
      .then(setPresets)
      .catch(() => setError('Could not load presets'));
  }, []);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give the preset a name first');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const preset = await createPreset({
        name: trimmed,
        focus_duration: settings.focusDuration,
        short_break_duration: settings.shortBreakDuration,
        long_break_duration: settings.longBreakDuration,
        cycle_count: settings.cycleCount,
      });
      setPresets((current) =>
        current
          ? [preset, ...current.filter((p) => p.id !== preset.id)]
          : [preset],
      );
      setName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save preset');
    } finally {
      setBusy(false);
    }
  };

  const handleApply = (preset: TimerPreset) => {
    updateSettings({
      focusDuration: preset.focus_duration,
      shortBreakDuration: preset.short_break_duration,
      longBreakDuration: preset.long_break_duration,
      cycleCount: preset.cycle_count,
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await deletePreset(id);
      setPresets((current) => current?.filter((p) => p.id !== id) ?? null);
    } catch {
      setError('Could not delete preset');
    }
  };

  return (
    <div className="space-y-3">
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSave();
          }}
          placeholder="Name this preset…"
          maxLength={50}
          className="flex-1 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 focus:outline-none focus:border-indigo-400"
          aria-label="Preset name"
        />
        <button
          onClick={() => void handleSave()}
          disabled={busy || !name.trim()}
          className="flex items-center gap-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded px-3 transition-colors"
          aria-label="Save current settings as preset"
        >
          <Save className="w-4 h-4" />
          Save current
        </button>
      </div>

      {!presets && !error && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      )}

      {presets && presets.length === 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          No presets yet — set your durations, name them, and save.
        </p>
      )}

      {presets && presets.length > 0 && (
        <div className="space-y-1.5">
          {presets.map((preset) => (
            <div
              key={preset.id}
              className="flex items-center justify-between text-sm bg-white dark:bg-gray-700 rounded px-2.5 py-1.5"
            >
              <div className="min-w-0">
                <p className="font-medium text-gray-800 dark:text-gray-200 truncate">
                  {preset.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatSummary(preset)}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                <button
                  onClick={() => handleApply(preset)}
                  className="p-1.5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition-colors"
                  aria-label={`Apply preset ${preset.name}`}
                  title="Apply"
                >
                  <Play className="w-4 h-4" />
                </button>
                <button
                  onClick={() => void handleDelete(preset.id)}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                  aria-label={`Delete preset ${preset.name}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
